import { useState, useEffect, useRef } from 'react';
import api, { setStoredGalleryId } from '../api';
import { useGallery } from '../context/GalleryContext';
import NameModal from './NameModal';
import TurnstileWidget from './TurnstileWidget';
import { getUserName, saveUserNameFromModal, markNameModalSkipped, hasSkippedNameModal } from '../utils/userName';
import { isInIframe, sendMessageToParent, getAllowedOrigins, MESSAGE_TYPES } from '../utils/iframeCommunication';
import { galleryUrlLabel } from '../utils/galleryHost';
import {
  clearStoredGuestAuthFailure,
  GUEST_AUTHENTICATED_EVENT,
  getGuestAuthFailureMessage,
} from '../utils/guestAuthRedirect';
import packageJson from '../../package.json';
import { CONFIG_VERSION } from '../config/version';

const INVITE_CODE_MIN_LENGTH = 4;
const INVITE_CODE_MAX_LENGTH = 32;

const InviteCode = ({ setAuth, logo, authFailureReason }) => {
  const { galleryId } = useGallery();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [pendingName, setPendingName] = useState('');
  const turnstileRef = useRef(null);

  // Send version info to parent if in iframe
  useEffect(() => {
    const inIframe = isInIframe();
    if (inIframe) {
      console.log('InviteCode: Sending version to parent');
      // The parent origin is not yet known (no message received), so post the handshake to
      // each explicitly allowed origin rather than broadcasting to '*' (matches Dashboard.js).
      const allowedOrigins = getAllowedOrigins();
      const readyTargets = allowedOrigins === '*' ? ['*'] : allowedOrigins;
      readyTargets.forEach((origin) => {
        sendMessageToParent(MESSAGE_TYPES.GALLERY_READY, {
          ready: true,
          version: packageJson.version,
          configVersion: CONFIG_VERSION  // Centralized config version
        }, origin);
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const id = galleryId && String(galleryId).trim();
      if (!id) {
        setError(
          `Open the correct gallery link (${galleryUrlLabel('…')}), then enter your invite code.`
        );
        setLoading(false);
        return;
      }
      const turnstileToken = turnstileRef.current ? await turnstileRef.current.getToken() : null;
      const { data } = await api.post(`/galleries/${encodeURIComponent(id)}/auth`, {
        code,
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      localStorage.setItem('weddingToken', data.token);
      const authenticatedGalleryId = data.galleryId || id;
      setStoredGalleryId(authenticatedGalleryId);
      clearStoredGuestAuthFailure();
      window.dispatchEvent(
        new CustomEvent(GUEST_AUTHENTICATED_EVENT, {
          detail: { galleryId: authenticatedGalleryId },
        })
      );
      
      // Check if we should show the name modal
      const existingName = getUserName();
      const hasSkipped = hasSkippedNameModal();
      
      if (!existingName && !hasSkipped) {
        // Show name modal before setting auth
        setShowNameModal(true);
        setLoading(false);
      } else {
        // Proceed directly to dashboard
        setAuth(true);
      }
    } catch (err) {
      // Any failed submit may have consumed the single-use Turnstile token
      // (the guard runs before the handler), so always re-arm the challenge.
      if (turnstileRef.current) turnstileRef.current.reset();
      const status = err.response?.status;
      const data = err.response?.data;
      const apiError = data && typeof data === 'object' ? data.error : null;
      if (status === 429) {
        const serverMessage = typeof data === 'string' && data && !data.includes('<') ? data : null;
        setError(serverMessage || 'Too many attempts. Please wait a few seconds and try again.');
      } else if (typeof apiError === 'string' && apiError.startsWith('TURNSTILE')) {
        setError((data && data.message) || 'Verification failed. Please complete the check again.');
      } else {
        const responseMessage = typeof data === 'string' ? data : data?.message;
        setError(responseMessage || 'Error authenticating');
      }
      setLoading(false);
    }
  };

  const handleNameSave = () => {
    const savedName = saveUserNameFromModal(pendingName);
    if (savedName) {
      setShowNameModal(false);
      setAuth(true);
    }
  };

  const handleNameCancel = () => {
    markNameModalSkipped();
    setShowNameModal(false);
    setAuth(true);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logo} alt="Wedding Logo" className="w-48 h-48 mx-auto mb-6 object-contain" />
          <h1 className="text-3xl font-serif text-secondary mb-2">Welcome</h1>
          <p className="text-secondary-70 text-sm leading-relaxed">
            Enter your invite code to access the wedding gallery
          </p>
          {authFailureReason ? (
            <div role="status" className="mt-4 p-3 bg-accent-10 border border-accent-30 rounded-xl">
              <p className="text-secondary text-sm">
                {getGuestAuthFailureMessage(authFailureReason)}
              </p>
            </div>
          ) : null}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-surface/80 backdrop-blur-sm border border-accent-20 rounded-2xl p-6 shadow-lg">
          <div>
            <label htmlFor="invite-code" className="block text-sm font-medium text-secondary mb-2">
              Invite Code
            </label>
            <input
              id="invite-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, INVITE_CODE_MAX_LENGTH))}
              placeholder="Enter your invite code"
              className="w-full p-4 bg-neutral-5 border-2 border-neutral-20 rounded-xl text-center text-lg font-mono tracking-wider focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary-20 focus:bg-surface transition-all duration-200"
              minLength={INVITE_CODE_MIN_LENGTH}
              maxLength={INVITE_CODE_MAX_LENGTH}
              autoComplete="off"
              autoFocus
            />
          </div>

          <TurnstileWidget ref={turnstileRef} />

          <button
            type="submit"
            disabled={loading || code.length < INVITE_CODE_MIN_LENGTH}
            className="w-full p-4 bg-primary text-surface rounded-xl font-semibold shadow-lg hover:bg-primary-90 hover:shadow-xl disabled:bg-neutral-30 disabled:text-neutral-60 disabled:shadow-none disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                <span>Verifying...</span>
              </div>
            ) : (
              'Access Gallery'
            )}
          </button>

          {error && (
            <div className="p-4 bg-accent-10 border border-accent-30 rounded-xl">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                </svg>
                <p className="text-secondary text-sm">{error}</p>
              </div>
            </div>
          )}
        </form>

        <div className="mt-8 text-center">
          <p className="text-xs text-secondary-70 px-3 py-2 rounded-lg inline-block">
            Having trouble? Check your invitation for the correct code
          </p>
        </div>
      </div>

      {/* Name Modal */}
      <NameModal
        isOpen={showNameModal}
        pendingName={pendingName}
        onChangePendingName={setPendingName}
        onCancel={handleNameCancel}
        onSave={handleNameSave}
      />
    </div>
  );
};

export default InviteCode;
